/**
 * Prices tab — the caller's per-owner model rate overrides (spec §8.2).
 *
 * The console does NOT price anything. It does not bundle the rate catalog and
 * never multiplies tokens by a rate: an override is an INPUT the server applies
 * at read time, and every cost figure the console renders arrived already
 * computed. This tab is a plain CRUD surface over `/v1/prices`.
 *
 * Consistent with BudgetsTab: local `useState`, no router, Saga-themed classes
 * from index.css, and an in-page two-step delete confirm rather than
 * window.confirm (a browser modal blocks automation and the extension).
 *
 * Overrides are configuration, gated by the same write permission the ingest
 * plane uses. A server 403 on a mutation surfaces inline — it does NOT blank
 * the console.
 */

import { useState } from 'react';
import {
  usePrices, useUpsertPrice, useDeletePrice,
  type PriceOverride, type PriceUpsertInput,
} from '../lib/queries';

/**
 * Mirrors `MODEL_MAX_BYTES` in `lib/lambdas/shared/store.ts`, which the price
 * route enforces via `Buffer.byteLength`. The model becomes part of a DynamoDB
 * attribute NAME (`m#<model>#out`) and those cap at 255 bytes, so the limit is
 * 255 minus the longest wrapper the rollup writes. A longer model buckets to the
 * `watchtower:oversize-model` sentinel on ingest, so an override keyed on the real
 * name could never match a rollup — the server rejects it rather than storing
 * permanently inert configuration. Do NOT relax this to match a client-side value.
 *
 * BYTES, not `.length`: JS string length counts UTF-16 code units, so a 200-char
 * string of 2-byte characters is 400 bytes and the two units disagree by 2-4x.
 *
 * Duplicated rather than imported: `dashboard/` imports nothing from
 * `lib/lambdas/` at runtime and must not start — pulling in that module would
 * bundle the rate catalog into the console, which never prices anything.
 * A drift guard for this constant lives in PricesTab.test.tsx.
 */
const MODEL_MAX_BYTES = 249;

/** UTF-8 byte length — what the server measures. */
const modelByteLength = (s: string): number => new TextEncoder().encode(s).length;

/** The four rate fields, in the order the form and table render them. */
export const RATE_FIELDS = [
  { key: 'input_rate', label: 'Input rate' },
  { key: 'output_rate', label: 'Output rate' },
  { key: 'cache_read_rate', label: 'Cache read rate' },
  { key: 'cache_write_rate', label: 'Cache write rate' },
] as const;

export type RateKey = (typeof RATE_FIELDS)[number]['key'];

export interface PriceFormValues {
  model: string;
  /** Rates are kept as strings so a half-typed number never becomes NaN
   *  mid-edit. Blank means 0 — i.e. "this rate is absent". */
  input_rate: string;
  output_rate: string;
  cache_read_rate: string;
  cache_write_rate: string;
}

export const EMPTY_FORM: PriceFormValues = {
  model: '', input_rate: '', output_rate: '', cache_read_rate: '', cache_write_rate: '',
};

/** Blank → 0; anything unparseable → NaN (which validation rejects). */
function rateNumber(raw: string): number {
  return raw.trim() === '' ? 0 : Number(raw);
}

/**
 * Field-keyed messages; an empty object means valid. MIRRORS the server's
 * `validateOverride` (price-store.ts) so the common mistakes never round-trip
 * — the server remains authoritative and its 400 surfaces inline.
 *
 * The all-zero rejection is the load-bearing one: a zero rate is ABSENT, not
 * free, so an all-zero override would shadow both the catalog and the
 * producer's reported cost with a false $0.
 */
export function validatePriceForm(values: PriceFormValues): Record<string, string> {
  const errors: Record<string, string> = {};

  const model = values.model.trim();
  if (model === '') errors.model = 'Model is required.';
  else if (modelByteLength(model) > MODEL_MAX_BYTES) {
    errors.model = `Model must be at most ${MODEL_MAX_BYTES} bytes (UTF-8).`;
  }

  const rates: number[] = [];
  for (const field of RATE_FIELDS) {
    const n = rateNumber(values[field.key]);
    if (!Number.isFinite(n) || n < 0) {
      errors[field.key] = `${field.label} must be a number of 0 or more.`;
    }
    rates.push(n);
  }

  // Only meaningful once every rate parsed — otherwise the per-field message is
  // the accurate one.
  if (Object.keys(errors).some((k) => k !== 'model')) return errors;
  if (!rates.some((r) => r > 0)) {
    errors.rates = 'At least one rate must be greater than zero — a zero rate means absent, not free.';
  }

  return errors;
}

export function toUpsertInput(values: PriceFormValues): PriceUpsertInput {
  return {
    model: values.model.trim(),
    input_rate: rateNumber(values.input_rate),
    output_rate: rateNumber(values.output_rate),
    cache_read_rate: rateNumber(values.cache_read_rate),
    cache_write_rate: rateNumber(values.cache_write_rate),
  };
}

/** A rate in USD per token, without exponent notation. A ZERO rate renders as
 *  an em-dash: zero means the rate is absent, and `$0.00000000` would read as a
 *  deliberate free price. */
export function fmtRate(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 12, useGrouping: false });
}

/** Prefill the form from an existing override so "edit" is replace-in-place. */
function formFrom(o: PriceOverride): PriceFormValues {
  return {
    model: o.model,
    input_rate: String(o.input_rate),
    output_rate: String(o.output_rate),
    cache_read_rate: String(o.cache_read_rate),
    cache_write_rate: String(o.cache_write_rate),
  };
}

export default function PricesTab() {
  const { data, isLoading, isError, error, refetch } = usePrices();
  const upsert = useUpsertPrice();
  const remove = useDeletePrice();

  const [values, setValues] = useState<PriceFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const prices = data?.prices ?? [];

  const set = <K extends keyof PriceFormValues>(key: K, value: string) =>
    setValues((v) => ({ ...v, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const found = validatePriceForm(values);
    setErrors(found);
    // Nothing is sent when the client-side check fails — notably an all-zero
    // override never reaches the network.
    if (Object.keys(found).length > 0) return;
    try {
      await upsert.mutateAsync(toUpsertInput(values));
      setValues(EMPTY_FORM);
    } catch (err) {
      // The server is authoritative — surface its code without losing the form.
      setServerError(err instanceof Error ? err.message : String(err));
    }
  };

  const del = async (model: string) => {
    setServerError(null);
    try {
      await remove.mutateAsync(model);
      setConfirmDelete(null);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : String(err));
    }
  };

  const fieldError = (key: string) =>
    errors[key] ? <span className="budget-form-error" role="alert">{errors[key]}</span> : null;

  return (
    <section className="prices-tab" aria-label="Prices">
      <p className="note">
        Rates are USD per token and apply to your data only. They override the
        built-in catalog; costs are computed server-side when a run is read, so a
        change here re-prices history immediately — nothing is backfilled.
      </p>

      <form className="card budget-form price-form" onSubmit={submit} aria-label="Add or replace a price override">
        <label className="budget-field">
          <span>Model</span>
          {/* Explicit aria-label: fieldError renders inside this <label>, so the
              wrapper's computed accessible name would otherwise absorb the error
              text once validation fails. */}
          <input
            aria-label="Model"
            type="text"
            placeholder="claude-opus-4-5-20251101"
            value={values.model}
            onChange={(e) => set('model', e.target.value)}
          />
          {fieldError('model')}
        </label>

        {RATE_FIELDS.map((field) => (
          <label className="budget-field" key={field.key}>
            <span>{field.label}</span>
            {/* Explicit aria-label: same fieldError-inside-<label> issue as Model. */}
            <input
              aria-label={field.label}
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={values[field.key]}
              onChange={(e) => set(field.key, e.target.value)}
            />
            {fieldError(field.key)}
          </label>
        ))}

        {errors.rates && <p className="error" role="alert">{errors.rates}</p>}
        {serverError && <p className="error" role="alert">Save failed: {serverError}</p>}

        <div className="budget-form-actions">
          <button type="submit" className="btn" disabled={upsert.isPending}>Save override</button>
          <button
            type="button"
            className="btn"
            onClick={() => { setValues(EMPTY_FORM); setErrors({}); setServerError(null); }}
          >
            Clear
          </button>
        </div>
      </form>

      <h3 className="section-title">Overrides</h3>
      {isLoading ? (
        <p className="note">Loading price overrides…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load price overrides: {error instanceof Error ? error.message : 'unknown error'}{' '}
          <button type="button" className="btn" onClick={() => refetch()}>Retry</button>
        </p>
      ) : prices.length === 0 ? (
        <p className="note">No price overrides. Every model is priced from the built-in catalog.</p>
      ) : (
        <table className="detail-table prices-table">
          <thead>
            <tr>
              <th>model</th>
              {RATE_FIELDS.map((f) => (
                <th key={f.key} className="detail-num">{f.label.toLowerCase()}</th>
              ))}
              <th>updated</th>
              <th>actions</th>
            </tr>
          </thead>
          <tbody>
            {prices.map((price) => (
              <tr key={price.model}>
                <td className="mono">{price.model}</td>
                {RATE_FIELDS.map((f) => (
                  <td key={f.key} className="detail-num mono">{fmtRate(price[f.key])}</td>
                ))}
                <td className="mono">{price.updated_at ?? '—'}</td>
                <td className="budgets-actions">
                  <button
                    type="button"
                    className="btn"
                    aria-label={`Edit ${price.model}`}
                    onClick={() => {
                      setConfirmDelete(null);
                      setErrors({});
                      setServerError(null);
                      setValues(formFrom(price));
                    }}
                  >
                    Edit
                  </button>
                  {confirmDelete === price.model ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      aria-label={`Confirm delete ${price.model}`}
                      onClick={() => void del(price.model)}
                    >
                      Confirm
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      aria-label={`Delete ${price.model}`}
                      // Arming a different row implicitly disarms any other —
                      // only one row may carry the destructive Confirm state.
                      onClick={() => setConfirmDelete(price.model)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
